export type BaiduNamespace = {
  Map: new (container: HTMLElement, options?: { enableMapClick?: boolean }) => unknown
  Overlay: new () => unknown
  Point: new (lng: number, lat: number) => unknown
  Marker: new (point: unknown, options?: unknown) => unknown
  Size: new (width: number, height: number) => unknown
  Icon: new (url: string, size: unknown) => unknown
}

export type GoogleNamespace = {
  maps: any
}

export const BAIDU_MAP_AK = 'gVako2Nm7CtU0EO7YivNmjpmW6wnSMWX'

export const PROD_GOOGLE_MAP_KEY = 'AIzaSyDQsHZefM8bTUX6tcm0Qsfrm7V9O9TPc9A'

export const LOCAL_GOOGLE_MAP_KEY = 'AIzaSyBdcQFTH4XHQs_U67iGJdf_SoHlIM6EZKo'

let baiduScriptPromise: Promise<BaiduNamespace> | null = null

let googleScriptPromise: Promise<GoogleNamespace> | null = null

function ensureGoogleMapsNamespace() {
  if (!window.google?.maps) {
    throw new Error('Google Maps SDK loaded without maps namespace')
  }

  if (typeof window.google.maps.Map === 'function') {
    return window.google as GoogleNamespace
  }

  throw new Error('Google Maps SDK loaded without Map constructor')
}

export function getSurveyGoogleMapKey() {
  if (import.meta.server) {
    return PROD_GOOGLE_MAP_KEY
  }

  const hostname = window.location.hostname
  const isLocalhost =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '192.168.122.102'

  return isLocalhost ? LOCAL_GOOGLE_MAP_KEY : PROD_GOOGLE_MAP_KEY
}

export function loadBaiduMapSdk(ak: string = BAIDU_MAP_AK) {
  if (
    typeof window !== 'undefined' &&
    (
      window as typeof window & {
        BMap?: BaiduNamespace
      }
    ).BMap
  ) {
    return Promise.resolve(
      (
        window as typeof window & {
          BMap: BaiduNamespace
        }
      ).BMap,
    )
  }

  if (baiduScriptPromise) {
    return baiduScriptPromise
  }

  baiduScriptPromise = new Promise((resolve, reject) => {
    const callbackName = '__wikibitBaiduMapInit'
    const scriptId = 'wikibit-baidu-map-sdk'
    const existedScript = document.getElementById(scriptId) as HTMLScriptElement | null

    const handleReady = () => {
      const BMap = (
        window as typeof window & {
          BMap?: BaiduNamespace
        }
      ).BMap

      if (!BMap) {
        reject(new Error('Baidu Maps SDK loaded without BMap namespace'))
        return
      }

      resolve(BMap)
    }

    ;(
      window as typeof window & {
        [callbackName]: () => void
      }
    )[callbackName] = handleReady

    if (existedScript) {
      existedScript.addEventListener('load', handleReady, { once: true })
      existedScript.addEventListener('error', () => reject(new Error('Baidu Maps SDK failed to load')), { once: true })
      return
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.async = true
    script.defer = true
    script.src = `https://api.map.baidu.com/api?v=3.0&ak=${ak}&callback=${callbackName}`
    script.addEventListener('error', () => reject(new Error('Baidu Maps SDK failed to load')), { once: true })
    document.head.appendChild(script)
  })

  return baiduScriptPromise
}

export function loadGoogleMapSdk(key: string = getSurveyGoogleMapKey()) {
  if (typeof window !== 'undefined' && window.google?.maps?.Map) {
    return Promise.resolve(window.google as GoogleNamespace)
  }

  if (googleScriptPromise) {
    return googleScriptPromise
  }

  googleScriptPromise = new Promise((resolve, reject) => {
    const callbackName = '__wikibitGoogleMapInit'
    const scriptId = 'wikibit-google-map-sdk'
    const existedScript = document.getElementById(scriptId) as HTMLScriptElement | null

    const cleanup = () => {
      delete (
        window as typeof window & {
          [callbackName]?: () => void
        }
      )[callbackName]
    }

    const resolveGoogle = () => {
      try {
        const googleNamespace = ensureGoogleMapsNamespace()
        cleanup()
        resolve(googleNamespace)
      } catch (error) {
        rejectGoogle(error instanceof Error ? error : new Error('Google Maps SDK failed to initialize'))
      }
    }

    const rejectGoogle = (error: Error) => {
      cleanup()
      googleScriptPromise = null
      reject(error)
    }

    ;(
      window as typeof window & {
        [callbackName]: () => void
      }
    )[callbackName] = resolveGoogle

    if (existedScript) {
      if (window.google?.maps?.Map) {
        resolveGoogle()
        return
      }

      existedScript.addEventListener('error', () => rejectGoogle(new Error('Google Maps SDK failed to load')), {
        once: true,
      })
      return
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.async = true
    script.defer = true
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&callback=${callbackName}&v=weekly`
    script.addEventListener('error', () => rejectGoogle(new Error('Google Maps SDK failed to load')), { once: true })
    document.head.appendChild(script)
  })

  return googleScriptPromise
}
